import { Modal, message } from 'antd';
import { FC, useEffect, useRef, useState } from 'react';
import { UpdateButton } from '../../../../../lib/components/Buttons/UpdateButton';
import { InputItem } from '../../../../../lib/components/InputItem';
import {
	isValidCpf,
	maskCpf,
	sanitizeCpf,
} from '../../../../../lib/utils/cpfValidator';
import { Nationality, SearchTagDto } from '../dto/SearchTagDto';
import {
	findSearchTagByCpf,
	submitWithOverwriteConfirm,
	updateSearchTag,
} from '../hooks/SearchTagsApi';
import { AuthorExtraFields } from './AuthorExtraFields';

interface IProps {
	visible: boolean;
	close: () => void;
	update_success: () => void;
	tag: SearchTagDto | null;
	tag_name_in_portuguese: string;
}

const TAG_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s.,\/ºª°-]+$/u;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CPF_CHECK_DEBOUNCE_MS = 400;

function deduceNationality(tag: SearchTagDto | null): Nationality {
	if (!tag) return 'Brasileiro';
	if (tag.nationality) return tag.nationality;
	if (tag.cpf) return 'Brasileiro';
	if (tag.document) return 'Estrangeiro';
	return 'Brasileiro';
}

export const SearchTagEditModal: FC<IProps> = ({
	visible,
	close,
	update_success,
	tag,
	tag_name_in_portuguese,
}) => {
	const isAuthor = tag?.prefix === 'author';

	const [tag_title, set_tag_title] = useState('');
	const [nationality, set_nationality] = useState<Nationality>('Brasileiro');
	const [document_value, set_document_value] = useState('');
	const [email, set_email] = useState('');
	const [cpf_check_loading, set_cpf_check_loading] = useState(false);
	const [cpf_conflict, set_cpf_conflict] = useState<{
		title: string;
		id: string;
	} | null>(null);
	const [loading, set_loading] = useState(false);
	const cpf_debounce_ref = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (visible && tag) {
			set_tag_title(tag.title || '');
			const deduced = deduceNationality(tag);
			set_nationality(deduced);
			if (deduced === 'Brasileiro') {
				set_document_value(tag.cpf ? maskCpf(tag.cpf) : '');
			} else {
				set_document_value(tag.document || '');
			}
			set_email(tag.email || '');
			set_cpf_conflict(null);
		}
		if (!visible) {
			set_tag_title('');
			set_nationality('Brasileiro');
			set_document_value('');
			set_email('');
			set_cpf_conflict(null);
			set_cpf_check_loading(false);
		}
	}, [visible, tag]);

	useEffect(() => {
		if (cpf_debounce_ref.current) clearTimeout(cpf_debounce_ref.current);
		set_cpf_conflict(null);

		if (!isAuthor || !tag || nationality !== 'Brasileiro') return;
		const sanitized = sanitizeCpf(document_value);
		if (sanitized.length !== 11 || !isValidCpf(sanitized)) return;

		set_cpf_check_loading(true);
		cpf_debounce_ref.current = setTimeout(async () => {
			try {
				const existing = await findSearchTagByCpf(sanitized);
				if (existing && existing.id !== tag.id) {
					set_cpf_conflict({ title: existing.title, id: existing.id });
				}
			} catch (err) {
				console.error('Erro ao verificar CPF:', err);
			} finally {
				set_cpf_check_loading(false);
			}
		}, CPF_CHECK_DEBOUNCE_MS);

		return () => {
			if (cpf_debounce_ref.current) clearTimeout(cpf_debounce_ref.current);
		};
	}, [document_value, isAuthor, nationality, tag]);

	if (!tag) return null;

	const sanitizedCpf = sanitizeCpf(document_value);
	const cpfFormatValid =
		nationality === 'Brasileiro' &&
		sanitizedCpf.length === 11 &&
		isValidCpf(sanitizedCpf);
	const emailFilled = email.trim().length > 0;
	const emailFormatValid = EMAIL_REGEX.test(email.trim());
	const documentReady =
		nationality === 'Brasileiro' ? cpfFormatValid : true;
	const emailReady =
		nationality === 'Estrangeiro'
			? emailFilled && emailFormatValid
			: !emailFilled || emailFormatValid;

	const submit = async () => {
		const trimmed = tag_title.trim();
		if (!trimmed) {
			message.warning('Informe o título.');
			return;
		}
		if (!TAG_REGEX.test(trimmed)) {
			message.error(`Título inválido: ${trimmed}`);
			return;
		}

		if (isAuthor) {
			if (nationality === 'Brasileiro' && !cpfFormatValid) {
				message.error('Informe um CPF válido.');
				return;
			}
			if (nationality === 'Estrangeiro' && !emailFilled) {
				message.error('Email é obrigatório para autores estrangeiros.');
				return;
			}
			if (emailFilled && !emailFormatValid) {
				message.error('Informe um email válido.');
				return;
			}
			if (cpf_conflict) {
				message.error(
					`Já existe um autor cadastrado com este CPF: ${cpf_conflict.title}.`
				);
				return;
			}
		}

		set_loading(true);
		const payload: any = {
			id: tag.id,
			title: trimmed,
			prefix: tag.prefix,
		};
		if (isAuthor) {
			payload.nationality = nationality;
			payload.email = emailFilled ? email.trim() : undefined;
			if (nationality === 'Brasileiro') {
				payload.cpf = sanitizedCpf;
				payload.document = undefined;
			} else {
				payload.cpf = undefined;
				payload.document = document_value.trim() || undefined;
			}
		}

		const outcome = await submitWithOverwriteConfirm(updateSearchTag, payload);
		set_loading(false);

		if (outcome.kind === 'ok') {
			message.success(`${tag_name_in_portuguese} atualizada com sucesso!`);
			update_success();
			close();
			return;
		}
		if (outcome.kind === 'cancelled') {
			message.info('Atualização cancelada.');
			return;
		}
		const err: any = outcome.error;
		const status = err?.response?.status;
		const code = err?.response?.data?.code;
		if (status === 409 && code === 'cpf-conflict') {
			const existing = err?.response?.data?.existing;
			message.error(
				`Já existe um autor cadastrado com este CPF: ${existing?.title || ''}.`
			);
		} else if (status === 409 && code === 'document-conflict') {
			const existing = err?.response?.data?.existing;
			message.error(
				`Já existe um autor cadastrado com este documento: ${existing?.title || ''}.`
			);
		} else {
			console.error('Erro ao atualizar tag:', err);
			message.error('Erro ao atualizar.');
		}
	};

	const submitDisabled =
		!tag_title ||
		loading ||
		cpf_check_loading ||
		(isAuthor && (!documentReady || !emailReady || !!cpf_conflict));

	return (
		<Modal
			{...{
				visible,
				centered: true,
				closable: true,
				destroyOnClose: true,
				onCancel: () => close(),
				title: `Editar ${tag_name_in_portuguese}`,
				footer: [
					<UpdateButton
						{...{
							onClick: () => submit(),
							disabled: submitDisabled,
						}}
					/>,
				],
			}}
		>
			<InputItem
				{...{
					label: isAuthor ? 'Nome do autor' : 'Título',
					on_changed: set_tag_title,
					input_item_name: tag_title,
				}}
			/>

			{isAuthor && (
				<AuthorExtraFields
					nationality={nationality}
					onNationalityChange={(n) => {
						set_nationality(n);
						set_document_value('');
						set_cpf_conflict(null);
					}}
					document={document_value}
					onDocumentChange={set_document_value}
					email={email}
					onEmailChange={set_email}
					cpfCheckLoading={cpf_check_loading}
					cpfConflictTitle={cpf_conflict?.title || null}
					disabled={loading}
				/>
			)}
		</Modal>
	);
};
